import { useMemo } from 'react';
import type { TableProps } from 'antd/es/table';

import type { AdminAssessmentResultsQuery } from '@evaltick/shared';

import { DataTable } from '@/components/DataTable';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { AdminService, type AdminAssessment } from '@/services/adminService';
import { ADMIN_TABLE_PAGE_SIZE } from '@/utils/constants/admin';

import { AssessmentsFilterBar } from './AssessmentsFilterBar';
import { buildAssessmentColumns } from './assessmentColumns';
import {
  ASSESSMENT_FLAGGED_OPTIONS,
  ASSESSMENT_SCORE_BAND_OPTIONS,
  ASSESSMENT_STATUS_OPTIONS,
} from './resultsFilterOptions';
import { assessmentMobileRow } from './resultsMobileRows';
import { useAssessmentsResultsFilters } from './useResultsListFilters';
import { useResultsTableData } from './useResultsTableData';
import { useSortOrderState } from './useSortOrderState';

// Module-level so this fetch's identity is stable across renders — an inline
// arrow passed straight into useResultsTableData would be a new function on
// every render, breaking its effect's memoization and re-fetching in a loop.
const fetchAssessmentsPage = (service: AdminService, page: number, filters: AdminAssessmentResultsQuery) =>
  service.listAssessments(page, ADMIN_TABLE_PAGE_SIZE, filters);

interface AssessmentsResultsPanelProps {
  urlPrefix: string;
  onView: (assessmentId: string) => void;
  rowSelection?: TableProps<AdminAssessment>['rowSelection'];
}

/**
 * The Assessments tab of the admin Results page: its own filter bar over its
 * own server-paginated, server-sorted table. Filtering/sorting happen
 * server-side — the table only ever holds one page, so doing either
 * client-side would miss every other page.
 */
export function AssessmentsResultsPanel({ urlPrefix, onView, rowSelection }: AssessmentsResultsPanelProps) {
  const { filters, page, apiFilters, hasActiveFilters, updateFilters, setPage, clearFilters } =
    useAssessmentsResultsFilters(urlPrefix);
  const { sortOrder, handleTableChange } = useSortOrderState(setPage);

  const search = useDebouncedSearch((value) => updateFilters({ search: value }), undefined, filters.search);

  const requestFilters = useMemo(
    () => ({ ...apiFilters, sortOrder: sortOrder === 'ascend' ? ('asc' as const) : sortOrder === 'descend' ? ('desc' as const) : undefined }),
    [apiFilters, sortOrder],
  );

  const { rows, totalCount, loading } = useResultsTableData(fetchAssessmentsPage, requestFilters, page, setPage, 'assessments');

  const columns = useMemo(() => buildAssessmentColumns(onView, sortOrder), [onView, sortOrder]);

  const handleClear = () => {
    // Drops keystrokes still waiting to apply and clears the input directly
    // — the emptied URL alone wouldn't rerun the sync effect if the search
    // param was already empty when a pending keystroke got cancelled.
    search.resetSearch();
    clearFilters();
  };

  return (
    <div>
      <AssessmentsFilterBar
        searchValue={search.searchInput}
        onSearchChange={search.handleSearchChange}
        statusOptions={ASSESSMENT_STATUS_OPTIONS}
        status={filters.status}
        onStatusChange={(status) => updateFilters({ status })}
        scoreBandOptions={ASSESSMENT_SCORE_BAND_OPTIONS}
        scoreBand={filters.scoreBand}
        onScoreBandChange={(scoreBand) => updateFilters({ scoreBand })}
        flaggedOptions={ASSESSMENT_FLAGGED_OPTIONS}
        flagged={filters.flagged}
        onFlaggedChange={(flagged) => updateFilters({ flagged })}
        from={filters.from}
        to={filters.to}
        onDateRangeChange={(from, to) => updateFilters({ from, to })}
        hasActiveFilters={hasActiveFilters || search.searchInput.trim() !== ''}
        onClear={handleClear}
      />
      <DataTable<AdminAssessment>
        dataSource={rows}
        columns={columns}
        rowKey="id"
        loading={loading}
        page={page}
        totalCount={totalCount}
        pageSize={ADMIN_TABLE_PAGE_SIZE}
        onPageChange={setPage}
        onChange={handleTableChange as TableProps<AdminAssessment>['onChange']}
        rowSelection={rowSelection}
        mobileRow={(record) => assessmentMobileRow(record, onView)}
        locale={{ emptyText: hasActiveFilters ? 'No assessments match these filters' : 'No assessments yet' }}
      />
    </div>
  );
}
